import { Card, CardContent } from "@/components/ui/card";
import type { ProjectFinancialSummary } from "@/generated/types";
import Currency from "./Currency";

interface ProjectInfoFinancialSummaryProps {
  summary: ProjectFinancialSummary | null;
  loading?: boolean;
}

interface SummaryMetricProps {
  label: string;
  children: React.ReactNode;
  valueClassName?: string;
  title?: string;
}

function SummaryMetric({ label, children, valueClassName, title }: SummaryMetricProps) {
  return (
    <div className="min-w-0 space-y-1.5">
      <div className="text-[0.65rem] uppercase tracking-widest text-primary-600 dark:text-primary-400 leading-snug">
        {label}
      </div>
      <div
        title={title}
        className={`text-sm font-light tabular-nums leading-none whitespace-nowrap overflow-hidden text-ellipsis ${
          valueClassName || "text-primary-950 dark:text-white"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function formatBrl(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function SummarySection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <h4 className="text-xs uppercase tracking-widest text-primary-500 dark:text-primary-500 border-b border-primary-100 dark:border-primary-800 pb-2">
        {title}
      </h4>
      <div className="grid grid-cols-2 gap-x-6 gap-y-5">{children}</div>
    </section>
  );
}

function SummarySkeleton() {
  return (
    <Card>
      <CardContent className="p-8 space-y-8">
        <div className="h-6 w-48 animate-pulse bg-primary-100 dark:bg-primary-800" />
        <div className="space-y-4">
          <div className="h-4 w-32 animate-pulse bg-primary-100 dark:bg-primary-800" />
          <div className="grid grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-14 animate-pulse bg-primary-100 dark:bg-primary-800" />
            ))}
          </div>
        </div>
        <div className="space-y-4">
          <div className="h-4 w-24 animate-pulse bg-primary-100 dark:bg-primary-800" />
          <div className="grid grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-14 animate-pulse bg-primary-100 dark:bg-primary-800" />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ProjectInfoFinancialSummary({
  summary,
  loading = false,
}: ProjectInfoFinancialSummaryProps) {
  if (loading || !summary) {
    return <SummarySkeleton />;
  }

  const showPending =
    summary.pendingInstallmentsCount > 0 && summary.totalPending > 0;
  const showOverdueValue = summary.totalOverdue > 0;
  const showSaldoAtrasado = summary.overdueInstallmentsCount > 0;

  return (
    <Card>
      <CardContent className="p-8 space-y-8">
        <h3 className="text-lg font-light text-primary-950 dark:text-white">
          RESUMO FINANCEIRO
        </h3>

        <SummarySection title="Quantidades">
          <SummaryMetric label="Contratos">{summary.contractsCount}</SummaryMetric>
          <SummaryMetric label="Parcelas">{summary.installmentsCount}</SummaryMetric>
          <SummaryMetric label="Pagas">{summary.paidInstallmentsCount}</SummaryMetric>
          {showPending ? (
            <SummaryMetric label="Pendentes">
              {summary.pendingInstallmentsCount}
            </SummaryMetric>
          ) : null}
          <SummaryMetric label="Atrasadas">
            {summary.overdueInstallmentsCount}
          </SummaryMetric>
        </SummarySection>

        <SummarySection title="Valores">
          <SummaryMetric
            label="Valor total"
            title={formatBrl(summary.totalContractsValue)}
          >
            <Currency value={summary.totalContractsValue} />
          </SummaryMetric>
          <SummaryMetric label="Pago" title={formatBrl(summary.totalPaid)}>
            <Currency value={summary.totalPaid} status="success" />
          </SummaryMetric>
          {showPending ? (
            <SummaryMetric
              label="Pendente"
              valueClassName="text-primary-800 dark:text-primary-200"
              title={formatBrl(summary.totalPending)}
            >
              <Currency value={summary.totalPending} />
            </SummaryMetric>
          ) : null}
          {showOverdueValue ? (
            <SummaryMetric label="Atrasado" title={formatBrl(summary.totalOverdue)}>
              <Currency value={summary.totalOverdue} status="overdue" />
            </SummaryMetric>
          ) : null}
        </SummarySection>

        <div className="space-y-4 border-t border-primary-200 dark:border-primary-800 pt-5">
          <div className="flex items-center justify-between gap-4 min-w-0">
            <span className="shrink-0 text-[0.65rem] uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Saldo restante
            </span>
            <span
              title={formatBrl(summary.balance)}
              className="min-w-0 text-right whitespace-nowrap overflow-hidden text-ellipsis"
            >
              <Currency value={summary.balance} />
            </span>
          </div>
          {showSaldoAtrasado ? (
            <div className="flex items-center justify-between gap-4 min-w-0">
              <span className="shrink-0 text-[0.65rem] uppercase tracking-widest text-primary-600 dark:text-primary-400">
                Saldo atrasado
              </span>
              <span
                title={formatBrl(summary.totalOverdue)}
                className="min-w-0 text-right whitespace-nowrap overflow-hidden text-ellipsis"
              >
                <Currency value={summary.totalOverdue} status="overdue" />
              </span>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
